import React, { ReactElement, useEffect, useState, useMemo } from 'react';
import { Heading } from 'rebass/styled-components';

import { SlidePanel, Loader, FormGroup } from 'ui/patterns';
import {
  Button,
  Stack,
  Separator,
  VisuallyHidden,
  StyledFormInput,
} from 'ui/components';
import { ProcedureAccordion } from 'ui/patterns';
import { ReactSelect } from 'ui/patterns/reactSelect';
import { EmptyState } from 'ui/patterns';

import {
  useGetExistingTemplates,
  useDeleteTemplate,
  useEditTemplateName,
  useSaveTemplate,
  useUpdateTemplate,
  useDeleteTemplateGroup,
  useEditTemplateGroupName,
} from 'hooks';

import { showErrorToast, showSuccessToast } from 'utils/showToast';
import { confirmAlert } from 'utils/confirm-alert';

import * as Icon from 'assets/icons';
import NoDataImage from 'assets/images/NoDataImage';

import { BlobType } from 'types/procedureBuilder';
import { ExistingLayoutProps } from './existingLayoutTypes';
import { Group } from '../procedureTypes';
import { ErrorModal } from 'types/modalTypes';

function ExistingLayout({
  showPanel,
  togglePanel: toggle,
  accountId,
}: ExistingLayoutProps): ReactElement {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState('');
  const [insertDisable, setInsertDisable] = useState(false);

  /*Grouping */
  const [isGroupEdited, setIsGroupEdited] = useState(false); // to handle changes in dropdown after group name change
  const [editGroupName, setEditGroupName] = useState(false); // group name edit state
  const [groupName, setGroupName] = useState<string>('All'); // group name & header
  const [selectedGroupValue, setSelectedGroupValue] = useState<Group>({
    label: ' All',
    value: 'All',
  }); // drop down selection
  const [selectedGroupLayoutList, setSelectedGroupLayoutList] = useState<
    Array<BlobType>
  >([]); //selected list
  /*Grouping */

  const { mutate: deleteTemplate, isLoading: isDeleteTemplateLoading } =
    useDeleteTemplate();

  const { mutate: editTemplateName, isLoading: isEditTemplateNameLoading } =
    useEditTemplateName();

  const { mutate: deleteGroup, isLoading: isDeleteLayoutGroupLoading } =
    useDeleteTemplateGroup();

  const { mutate: editGroupTitle, isLoading: isEditGroupTitleLoading } =
    useEditTemplateGroupName();

  const { data: layoutData, isLoading: layoutDataLoading } =
    useGetExistingTemplates({ showPanel, accountId, templateType: 'layouts' });

  const { mutate: saveTemplate, isLoading: isSaveTemplateLoading } =
    useSaveTemplate();

  const { mutate: updateTemplate, isLoading: isUpdateTemplateLoading } =
    useUpdateTemplate();

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(showPanel);
    }, 10);
  }, [showPanel]);

  const folderOptions = useMemo(() => {
    return (
      layoutData &&
      layoutData.Data &&
      layoutData.Data[0]?.Blobs?.filter(
        (blob: BlobType) => blob.Type === 'folder',
      ).map((group: BlobType) => {
        return { label: group.Name, value: group.Name };
      })
    );
  }, [layoutData]);

  const layoutTemplateData = useMemo(() => {
    return layoutData && layoutData.Data && layoutData?.Data[0]?.Blobs;
  }, [layoutData]);

  //handing group changes in dropdown
  useEffect(() => {
    if (layoutTemplateData) {
      if (selectedGroupLayoutList.length >= 0 && selectedGroupValue !== null) {
        if (layoutTemplateData.length !== 0) {
          const temp: Array<BlobType> = [];
          if (selectedGroupValue.value === 'All') {
            layoutTemplateData?.map((layout: BlobType) => {
              if (layout.Type === 'html') {
                temp.push(layout);
              } else if (selectedGroupValue.value === layout.Name) {
                layout?.Blobs?.map((item: BlobType) => {
                  temp.push(item);
                });
              }
              setSelectedGroupLayoutList(temp);
            });
          } else {
            layoutTemplateData?.map((layout: BlobType) => {
              if (selectedGroupValue.value === layout.Name) {
                layout?.Blobs?.map((item: BlobType) => {
                  temp.push(item);
                });
                setSelectedGroupLayoutList(temp);
              }
            });
          }
        } else {
          setSelectedGroupLayoutList([]);
        }
      }
    }
  }, [
    folderOptions,
    selectedGroupLayoutList.length,
    selectedGroupValue,
    layoutTemplateData,
  ]);

  //reset header and dropdown selection to All after deleting group or last entry
  useEffect(() => {
    const value = folderOptions?.filter(
      (item: any) => item.label === selectedGroupValue.label,
    );
    if (
      value?.length === 0 &&
      selectedGroupValue.label !== 'All' &&
      !isGroupEdited
    ) {
      setGroupName('All');
      setSelectedGroupValue({ label: 'All', value: 'All' });
    }
  }, [folderOptions, isGroupEdited, selectedGroupValue.label]);

  const updatedGroups = useMemo(() => {
    return layoutTemplateData?.map((item: any) => {
      if (item.FileType === 'html') {
        item.mappedGroups = [];
      }
      return item;
    });
  }, [layoutTemplateData]);

  const mappedGrouping = () => {
    const ungrouped = updatedGroups?.filter(item => item.Type === 'html');
    const grouped = updatedGroups?.filter(item => item.Type !== 'html');
    if (grouped && ungrouped) {
      for (let j = 0; j < ungrouped.length; j++) {
        for (let i = 0; i < grouped?.length; i++) {
          grouped[i]?.BlobFile?.map((subItem: any) => {
            if (ungrouped[j].Name === subItem.Name) {
              if (!ungrouped[j]?.mappedGroups?.length)
                ungrouped[j]?.mappedGroups?.push(grouped[i]?.Name);
              else {
                const isGroupPresent = ungrouped[j]?.mappedGroups?.filter(
                  (item: any) => item === grouped[i]?.Name,
                );
                if (!isGroupPresent?.length)
                  ungrouped[j]?.mappedGroups?.push(grouped[i]?.Name);
              }
            }
          });
        }
      }
    }
  };

  function togglePanel() {
    setIsVisible(false);
    setTimeout(() => {
      toggle();
    }, 500);
  }

  const insertLayout = () => {
    let layoutInsert: Array<BlobType> | undefined = [];
    if (selectedGroupValue.value !== 'All') {
      const layoutGroup = layoutTemplateData?.filter(
        template =>
          template.Type === 'folder' &&
          template.Name === selectedGroupValue.label,
      );
      layoutInsert =
        layoutGroup &&
        layoutGroup[0]?.Blobs?.filter(
          (item: BlobType) => item.Name === selectedLayout,
        );
    } else {
      layoutInsert = layoutTemplateData
        ?.filter(data => data.Type === 'html')
        ?.filter(item => item.Name === selectedLayout);
    }
    const content = layoutInsert && layoutInsert[0]?.Content;
    if (content) (window as any)?.tinymce?.activeEditor?.insertContent(content);
  };

  //Table deletion affects only the respective folder
  const deleteLayout = (fileTitle: string) => {
    let isDeleteFromAllFolder: boolean;
    confirmAlert({
      message:
        selectedGroupValue.label === 'All'
          ? 'Are you sure you want to delete this layout?'
          : selectedGroupLayoutList.length === 1
          ? `As you are removing the last layout of group [${groupName}], it will be removed from the list of groups. Are you sure you want to remove this layout from the group?`
          : 'Are you sure you want to remove this layout from the group?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            if (groupName !== 'All') {
              fileTitle = `${groupName}/${fileTitle}`;
              isDeleteFromAllFolder = false;
            } else isDeleteFromAllFolder = true;
            if (fileTitle) {
              deleteTemplate(
                {
                  accountId,
                  params: {
                    Container: 'layouts',
                    Title: fileTitle,
                    IsDeleteFromAllFolder: isDeleteFromAllFolder,
                  },
                },
                {
                  onSuccess: () => {
                    setIsGroupEdited(false);
                    showSuccessToast({
                      message: 'Your changes were saved successfully.',
                    });
                  },
                  onError: (error: ErrorModal) => {
                    showErrorToast({ message: `${error.Errors[0].Message}` });
                  },
                },
              );
            }
          },
          className: 'pr-btn-primary',
        },
        {
          label: 'No',
          onClick: () => {
            //
          },
        },
      ],
    });
  };

  //Table name edit affects all tables with same name
  const editLayoutTitle = (oldFileName: string, newFileName: string) => {
    let newLayoutName = '';
    if (oldFileName !== newFileName) {
      if (!newFileName.includes('.html'))
        newLayoutName = `${newFileName.trim()}.html`;
      else newLayoutName = newFileName.trim();
      editTemplateName(
        {
          params: {
            RootContainer: 'layouts',
            ExistingFileName: oldFileName,
            NewlySuggestedFileName: newLayoutName,
          },
          accountId: accountId,
        },
        {
          onSuccess: () => {
            showSuccessToast({
              message: 'Your changes were saved successfully.',
            });
          },
          onError: (error: ErrorModal) => {
            showErrorToast({ message: `${error.Errors[0].Message}` });
          },
        },
      );
    }
  };

  const procedureActions = () => (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          togglePanel();
          setSelectedLayout('');
        }}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={() => {
          insertLayout();
          togglePanel();
          setSelectedLayout('');
        }}
        disabled={
          selectedLayout === '' || insertDisable === true ? true : false
        }
      >
        Insert
      </Button>
    </>
  );

  const existingLayoutCheck = (title: string | undefined) => {
    let checkedValue: Array<BlobType> | undefined = [];
    const check = title?.toLowerCase().includes('.html');
    let value: string | undefined;
    if (check) value = title?.split('.')[0]?.trim();
    else value = title?.trim();
    const layoutGroup = layoutTemplateData?.filter(
      (data: any) => data.FileType === 'html',
    );
    checkedValue = layoutGroup?.filter((item: any) => {
      if (item.FileName.split('.')[0] === value) return item;
    });
    if (checkedValue?.length) {
      return true;
    } else return false;
  };

  const updateLayout = (
    title: string,
    content: string | undefined,
    accountId: number,
  ) => {
    updateTemplate(
      {
        params: {
          Name: title.trim(),
          Content: content || '',
          ContainerName: 'layouts',
        },
        accountId: accountId,
      },
      {
        onSuccess: () => {
          showSuccessToast({ message: 'Layout saved successfully' });
        },
      },
    );
  };

  const deleteLayoutGroup = (title: string) => {
    setIsGroupEdited(true);
    confirmAlert({
      message: 'Are you sure you want to delete the group?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => {
            if (title) {
              deleteGroup(
                {
                  accountId,
                  params: {
                    Container: 'layouts',
                    Title: title,
                  },
                },
                {
                  onSuccess: () => {
                    setIsGroupEdited(false);
                    showSuccessToast({
                      message: 'Your changes were saved successfully.',
                    });
                  },
                  onError: (error: ErrorModal) => {
                    showErrorToast({ message: `${error.Errors[0].Message}` });
                  },
                },
              );
            }
          },
          className: 'pr-btn-primary',
        },
        {
          label: 'No',
          onClick: () => {
            //
          },
        },
      ],
    });
  };

  const handleGroupChange = (e: { label: string; value: string }) => {
    setIsGroupEdited(false);
    setSelectedGroupValue(e);
    setGroupName(e.value);
  };

  const handleGroupInsertion = (
    selectedGroups: Array<Group>,
    title: string,
    content: string | undefined,
    unselectedGroups: Array<Group>,
  ) => {
    const insertTitleTemp = selectedGroups.map((group: Group) => {
      return `${group.label}/${title}`;
    });
    const insertTitleString = insertTitleTemp.join(',');
    const removeTitleTemp = unselectedGroups?.map((group: Group) => {
      return `${group.label}/${title}`;
    });
    const removeTitleString = removeTitleTemp.join(',');
    saveTemplate({
      params: {
        Name: insertTitleString.trim(),
        Content: content || '',
        ContainerName: 'layouts',
      },
      accountId: accountId,
    });
    if (unselectedGroups.length !== 0) {
      deleteTemplate(
        {
          accountId,
          params: {
            Container: 'layouts',
            Title: removeTitleString.trim(),
            IsDeleteFromAllFolder: false,
          },
        },
        {
          onSuccess: () => {
            setIsGroupEdited(false);
          },
          onError: (error: ErrorModal) => {
            showErrorToast({ message: `${error.Errors[0].Message}` });
          },
        },
      );
    }
  };

  const handleLayoutGroupNameEdit = (
    oldGroupName: string,
    newGroupName: string,
  ) => {
    const isExists = folderOptions?.filter(
      (item: any) => item.label === newGroupName,
    );
    if (isExists?.length) {
      showErrorToast({
        message: 'Group name already exists.Please enter a different name',
      });
    }
    editGroupTitle(
      {
        params: {
          RootContainer: 'layouts',
          ExistingDirectoryName: oldGroupName,
          NewlySuggestedDirectoryName: newGroupName.trim(),
        },
        accountId: accountId,
      },
      {
        onSuccess: (message: any) => {
          if (typeof message === 'string') {
            showErrorToast({ message: `${message}` });
          } else {
            setIsGroupEdited(true);
            setSelectedGroupValue({
              label: `${newGroupName.trim()}`,
              value: `${newGroupName.trim()}`,
            });
            showSuccessToast({
              message: 'Your changes were saved successfully.',
            });
          }
        },
        onError: (error: any) => {
          showErrorToast({ message: `${error.message}` });
        },
      },
    );
  };

  return (
    <SlidePanel
      open={isVisible}
      onClose={() => {
        togglePanel();
        setSelectedLayout('');
      }}
      size="md"
      title="Select Layout"
      actions={procedureActions()}
    >
      <FormGroup>
        <ReactSelect
          name="layoutOptions"
          label="Group"
          isSearchable={true}
          isClearable={false}
          options={
            folderOptions !== undefined
              ? [{ label: 'All', value: 'All' }, ...folderOptions]
              : []
          }
          value={selectedGroupValue}
          classNamePrefix="pr-select"
          onChange={(e: any) => handleGroupChange(e)}
          onMenuOpen={() => setSelectedLayout('')}
        />
      </FormGroup>
      <Separator direction="x" my={5} />
      <Stack direction="x" gap={1} alignItems="center" mb={3}>
        {editGroupName ? (
          <Stack direction="x" gap={1} alignItems="center" mb={3}>
            <StyledFormInput
              placeholder="Group name"
              style={{ paddingInline: '0.75rem', width: '300px' }}
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
            />
            <Button
              size="sm"
              variant="ghost"
              iconBefore={<Icon.Check />}
              title="Save"
              onClick={() => {
                if (
                  !groupName ||
                  !groupName.trim() ||
                  groupName.toLowerCase() === 'all' ||
                  groupName.includes(',')
                ) {
                  showErrorToast({
                    message: 'Please enter a valid name',
                  });
                  return;
                } else {
                  setEditGroupName(false);
                  handleLayoutGroupNameEdit(
                    selectedGroupValue.label,
                    groupName,
                  );
                }
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              iconBefore={<Icon.X />}
              title="Cancel"
              onClick={() => {
                setEditGroupName(false);
                setGroupName(selectedGroupValue.label);
              }}
            />
          </Stack>
        ) : (
          <>
            <Heading as="h3" fontSize={3} fontWeight="medium">
              {groupName}
            </Heading>
            {groupName.toLowerCase() === 'all' ? null : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Edit group name"
                  onClick={() => setEditGroupName(true)}
                >
                  <Icon.Edit ariaHidden="true" />
                  <VisuallyHidden>Edit Group Name</VisuallyHidden>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Remove group"
                  onClick={() => deleteLayoutGroup(groupName)}
                >
                  <Icon.Delete />
                  <VisuallyHidden>Remove Group.</VisuallyHidden>
                </Button>
              </>
            )}
          </>
        )}
      </Stack>
      <Stack direction="y" gap={3}>
        {selectedGroupLayoutList?.map((layout, i) => (
          <ProcedureAccordion
            key={i}
            accountId={accountId}
            title={layout.Name}
            content={layout.Content}
            open={selectedLayout}
            container="layouts"
            setOpen={setSelectedLayout}
            deleteTemplate={deleteLayout}
            editTemplate={editLayoutTitle}
            templateExistingCheck={existingLayoutCheck}
            updateTemplate={updateLayout}
            setInsertDisable={setInsertDisable}
            templateGroups={folderOptions}
            handleGroupInsertion={handleGroupInsertion}
            mappedGrouping={mappedGrouping}
            ungroupedSet={updatedGroups?.filter(
              (item: any) => item.FileType === 'html',
            )}
            selectedGroupDropDown={selectedGroupValue}
          />
        ))}
      </Stack>
      {selectedGroupLayoutList?.length === 0 ? (
        <EmptyState>
          <NoDataImage feedback="No Layouts Available" />
        </EmptyState>
      ) : null}
      {(layoutDataLoading ||
        isDeleteTemplateLoading ||
        isEditTemplateNameLoading ||
        isDeleteLayoutGroupLoading ||
        isSaveTemplateLoading ||
        isUpdateTemplateLoading ||
        isEditGroupTitleLoading) && <Loader />}
    </SlidePanel>
  );
}

export default ExistingLayout;
